import React from 'react'
import { useDispatchedReducer, useCallback, useEffect, useRef } from 'sb-hooks'
import { useIntl } from 'sb-react-intl'

import UnderlineInputView from './UnderlineInputView/UnderlineInputView'
import StandardInputView from './StandardInputView/StandardInputView'


const fieldReducer = {
  initialState: {
    value: '',
    error: '',
    isValidating: false,
  },
  update: (state, values) => ({ ...state, ...values }),
  setValue: (state, value) => ({ ...state, value }),
  setValidationStatus: (state, status) => ({ ...state, isValidating: status }),
}


const useReducer = (field) => {
  const modifyInitialState = useCallback(({ value, error, ...rest }) => ({
    value: field.value || value,
    error: field.error || error,
    ...rest,
  }), [])

  return useDispatchedReducer(fieldReducer, modifyInitialState)
}

const useFieldSubscribers = ({ field, actions }) => {
  useEffect(() => {
    const handleFieldStartValidate = () => {
      actions.setValidationStatus(true)
    }

    const handleFieldValidate = (error) => {
      actions.update({
        isValidating: false,
        error,
      })
    }

    const handleChange = async (value) => {
      actions.setValue(value)

      // prevent field from validation until user blurred it once
      if (field.isValidated && field.validateAfterChange) {
        await field.debounceValidate()
      }
    }

    const handleUnset = () => {
      actions.setValue(field.value)
    }

    field.on('start validate', handleFieldStartValidate)
    field.on('validate', handleFieldValidate)
    field.on('change', handleChange)
    field.on('unset', handleUnset)

    return () => {
      field.off('start validate', handleFieldStartValidate)
      field.off('validate', handleFieldValidate)
      field.off('change', handleChange)
      field.off('unset', handleUnset)
    }
  }, [ field, actions ])
}

const useHandleChange = ({ field, pattern, mask, maskReplace, onChange }) =>
  useCallback((event) => {
    let { value } = event.target

    if (mask && value) {
      if (maskReplace) {
        value = value.replace(maskReplace, '')
      }
    }
    else if (pattern && value) {
      value = value.replace(new RegExp(`[^${pattern}]+`, 'g'), '')
    }

    field.set(value)

    if (typeof onChange === 'function') {
      onChange(value)
    }
  }, [ field, pattern, mask, maskReplace, onChange ])

const useHandleBlur = ({ field, onBlur }) =>
  useCallback(async (event) => {
    await field.debounceValidate()

    // // prevent input from blurring if there is no async validation
    // if (!field.hasAsyncValidators) {
    //   event.preventDefault()
    // }
    //
    // const error = await field.debounceValidate()
    //
    // // blur input if there is no error
    // if (!field.hasAsyncValidators && !error) {
    //   field.node.blur()
    // }

    if (typeof onBlur === 'function') {
      onBlur(event)
    }
  }, [ field, onBlur ])

const useFocusOnInit = ({ ref, isEnabled }) =>
  useEffect(() => {
    if (isEnabled) {
      ref.current.focus()
    }
  }, [ isEnabled, ref ])


export type FieldProps = {
  id?: string
  field: any
  standardView?: boolean
  inputRef?: React.MutableRefObject<HTMLInputElement>
  className?: string
  type?: string
  rows?: number
  autosize?: boolean
  autoComplete?: string
  label?: string | ReactIntlMessage
  placeholder?: string | ReactIntlMessage
  submitButtonMessage?: string | ReactIntlMessage
  pattern?: string
  icon?: string
  iconColor?: string
  iconButtonProps?: any
  loaderClassName?: string
  inputClassName?: string
  mask?: string | Function
  maskChar?: string
  maskGuide?: boolean
  maskReplace?: boolean
  errored?: boolean
  loading?: boolean
  readOnly?: boolean
  focusOnInit?: boolean
  blackOnFocus?: boolean
  withBorder?: boolean
  withEraser?: boolean
  semiboldPlaceholder?: boolean
  beforeNode?: React.ReactNode
  afterNode?: React.ReactNode
  onChange?: (value: string) => void
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onTouchStart?: React.TouchEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onSubmit?: Function
  onIconClick?: Function
  onBeforeNodeClick?: Function
  onAfterNodeClick?: Function
  onErase?: Function
  dataTestId?: string
  intl?: ReactIntl
}


const Field: React.FunctionComponent<FieldProps> = (props) => {
  const {
    className, loaderClassName, inputClassName,
    inputRef, field, type, label, placeholder, submitButtonMessage, pattern, rows, autosize,
    icon, iconColor,
    mask, maskChar, maskGuide, maskReplace,
    errored, loading, readOnly, focusOnInit, withBorder, withEraser, standardView, semiboldPlaceholder,
    beforeNode, afterNode,
    onSubmit, onIconClick, onChange, onFocus, onBlur, onErase, onTouchStart, onBeforeNodeClick, onAfterNodeClick,
    dataTestId, id,
    blackOnFocus, // test for /subscribe-prototype
    iconButtonProps,
    autoComplete,
  } = props

  const intl = useIntl()
  const [ state, actions ] = useReducer(field)
  const ref = useRef<HTMLInputElement>(null)

  useFieldSubscribers({ field, actions })
  useFocusOnInit({ ref, isEnabled: focusOnInit })

  const handleChange  = useHandleChange({ field, pattern, mask, maskReplace, onChange })
  const handleBlur    = useHandleBlur({ field, onBlur })
  const handleErase   = useCallback(() => {
    field.unset()

    if (typeof onErase === 'function') {
      onErase()
    }
  }, [ field, onErase ])

  useEffect(() => {
    // update value if user entered something to input rendered by SSR when client react is not initialized yet
    field.set(ref.current.value)

    return () => {
      field.unsetRef()
    }
  }, [ field ])

  const resolveRef = (node) => {
    ref.current = node

    if (inputRef) {
      inputRef.current = node
    }

    field.setRef(node)
  }

  const nodeProps: any = {
    id,
    className,
    inputClassName,
    loaderClassName,
    ref: resolveRef,
    type,
    label,
    value: state.value,
    error: state.error,
    pattern,
    mask,
    maskChar,
    maskGuide,
    errored,
    loading,
    readOnly,
    disabled: readOnly,
    withEraser,
    beforeNode,
    afterNode,
    blackOnFocus, // test for /subscribe-prototype
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus,
    onErase: handleErase,
    onTouchStart,
    onBeforeNodeClick,
    onAfterNodeClick,
    dataTestId,
    dataRecurly: field.name,
    iconButtonProps,
    icon,
    iconColor,
    onIconClick,
    autoComplete,
  }

  if (standardView) {
    nodeProps.placeholder = placeholder
    nodeProps.submitButtonMessage = submitButtonMessage
    nodeProps.withBorder = withBorder
    nodeProps.onSubmit = onSubmit
    nodeProps.rows = rows
    nodeProps.autosize = autosize
    nodeProps.semiboldPlaceholder = semiboldPlaceholder
  }

  nodeProps.eraserLabel = label ? `Clear ${intl.message(label)}` : 'Clear input'

  return React.createElement(standardView ? StandardInputView : UnderlineInputView, nodeProps)
}

Field.defaultProps = {
  // 'nope' was a hack to remove autoComplete, coz 'off' doesn't work
  // bug: https://bugs.chromium.org/p/chromium/issues/detail?id=914451
  // workarond: https://goo.gl/PeZ856
  // 'nope' is ignored? https://github.com/Rendez/downshift/commit/bf876844925545bab3cb32d6ff66c1beeb056199
  autoComplete: 'off',
}


export default Field
